import { SettingsCard } from '@components/SettingsCard'
import { User } from '@components/user/User'
import { Venue } from '@components/venue/Venue'
import {
  CustomMenuItemChildren,
  useSettingMenuItems,
} from '@hooks/useSettingMenuItems'
import { User as UserModel } from '@models/User'
import { Venue as VenueModel } from '@models/Venue'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getSearchUsers,
  getSearchVenues,
  resetSearch,
} from '@store/SearchReducer'
import { selectUsersByIds } from '@store/UserReducer'
import { selectVenuesByIds } from '@store/VenueReducer'
import { TableParams } from '@supplement-bacon/sb-table-filters'
import {
  Empty,
  Input,
  InputRef,
  List,
  Modal,
  Space,
  Typography,
  theme,
} from 'antd'
import { debounce } from 'lodash'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSearchDialog } from './context'
import SearchDialogModalFooter from './partial/modal-footer'

const { Title } = Typography

type SearchItem =
  | { type: 'Users'; data: UserModel }
  | { type: 'Venues'; data: VenueModel }
  | { type: 'Settings / Tools'; data: CustomMenuItemChildren }

interface Props {
  //
}

const SearchDialogModal: FC<Props> = ({}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { token } = theme.useToken()
  const { flatChildren } = useSettingMenuItems()
  const { isVisible, setIsVisible } = useSearchDialog()

  const inputRef = useRef<InputRef>(null)
  const itemRefs = useRef<Array<HTMLDivElement | null>>([])

  const {
    query,
    users: userSearch,
    venues: venueSearch,
  } = useAppSelector((state) => state.search)
  const users = useAppSelector(selectUsersByIds(userSearch.ids))
  const venues = useAppSelector(selectVenuesByIds(venueSearch.ids))

  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [filteredSettings, setFilteredSettings] = useState<
    CustomMenuItemChildren[]
  >([])

  const filteredItems: SearchItem[] = useMemo(
    () => [
      ...users.map((u) => ({ type: 'Users' as const, data: u })),
      ...venues.map((v) => ({ type: 'Venues' as const, data: v })),
      ...filteredSettings.map((s) => ({
        type: 'Settings / Tools' as const,
        data: s,
      })),
    ],
    [users, venues, filteredSettings]
  )

  /**
   * `query` and `flatChildren` are read through refs so the debounced search
   * can be created once. Keying it on them rebuilt the debounced function on
   * every render (`flatChildren` is a fresh array each time), which left each
   * keystroke with its own uncancelled timer: the dialog fired one request per
   * character and older responses could land last.
   */
  const queryRef = useRef(query)
  const flatChildrenRef = useRef(flatChildren)
  useEffect(() => {
    queryRef.current = query
  }, [query])
  useEffect(() => {
    flatChildrenRef.current = flatChildren
  }, [flatChildren])

  const handleSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (!value.trim()) {
          setFilteredSettings([])
          return
        }

        setFilteredSettings(
          flatChildrenRef.current.filter(
            (item) =>
              item.title.toLowerCase().includes(value.toLowerCase()) ||
              item.description.toLowerCase().includes(value.toLowerCase())
          )
        )

        const newQuery: TableParams = {
          ...queryRef.current,
          search: { name: value },
        }
        dispatch(getSearchVenues(newQuery))
        dispatch(getSearchUsers(newQuery))
      }, 300),
    [dispatch]
  )

  useEffect(() => {
    return () => {
      handleSearch.cancel()
    }
  }, [handleSearch])

  const onClose = () => {
    // A pending search would otherwise land after the reset and refill the list.
    handleSearch.cancel()
    setHighlightedIndex(0)
    setIsVisible(false)
    dispatch(resetSearch())
    setFilteredSettings([])
  }

  const handleSelect = (item: SearchItem) => {
    if (item.type === 'Users') {
      navigate(`/users/${item.data.id}`)
    } else if (item.type === 'Venues') {
      navigate(`/venues/${item.data.slug}/generals`)
    } else {
      navigate(`/settings/${item.data.url}`)
    }
    onClose()
  }

  useEffect(() => {
    setHighlightedIndex(0)
  }, [filteredItems.length])

  useEffect(() => {
    const onKeyboardShortcut = (e: KeyboardEvent) => {
      if (e.metaKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setIsVisible(true)
      }
    }
    window.addEventListener('keydown', onKeyboardShortcut)
    return () => window.removeEventListener('keydown', onKeyboardShortcut)
  }, [])

  useEffect(() => {
    if (!isVisible) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((i) => Math.min(i + 1, filteredItems.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        const item = filteredItems[highlightedIndex]
        if (item) {
          handleSelect(item)
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isVisible, filteredItems])

  useEffect(() => {
    const node = itemRefs.current[highlightedIndex]
    if (node) {
      node.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      })
    }
  }, [highlightedIndex])

  return (
    <Modal
      width={700}
      destroyOnHidden
      open={isVisible}
      closable={false}
      onCancel={onClose}
      footer={<SearchDialogModalFooter />}
      afterOpenChange={() => inputRef.current?.focus({ cursor: 'start' })}
      title={
        <Input
          ref={inputRef}
          size="large"
          onChange={(e) => {
            handleSearch(e.target.value)
          }}
          placeholder="Search venues, users, or settings..."
        />
      }
    >
      {filteredItems.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No results found"
          style={{ padding: 50 }}
        />
      ) : (
        <List
          dataSource={filteredItems}
          style={{ maxHeight: '40vh', overflowY: 'auto' }}
          loading={userSearch.isLoading || venueSearch.isLoading}
          renderItem={(item, index) => {
            const isNewSection =
              index === 0 || item.type !== filteredItems[index - 1].type

            return (
              <>
                {isNewSection && (
                  <Space
                    className="w-full space-between"
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      padding: '10px 0',
                      background: token.colorBgElevated,
                    }}
                  >
                    <Title level={5} className="m-0">
                      {item.type}
                    </Title>
                  </Space>
                )}

                <List.Item
                  /** Even though the avatars below bring to the detail page we want to redirect when the user is clicking on the item outside of the avatar */
                  onClick={() => handleSelect(item)}
                  ref={(item) => (itemRefs.current[index] = item)}
                  style={{
                    padding: 12,
                    borderRadius: token.borderRadius,
                    border:
                      index === highlightedIndex
                        ? `2px solid ${token.colorPrimary}`
                        : undefined,
                    backgroundColor:
                      index === highlightedIndex
                        ? token.colorBgTextHover
                        : undefined,
                  }}
                >
                  {item.type === 'Venues' && <Venue.Avatar id={item.data.id} />}
                  {item.type === 'Users' && <User.Avatar id={item.data.id} />}
                  {item.type === 'Settings / Tools' && (
                    <SettingsCard
                      item={item.data}
                      color={item.data.color}
                      displayMode="list"
                    />
                  )}
                </List.Item>
              </>
            )
          }}
        />
      )}
    </Modal>
  )
}

type SearchDialogModalType = { Modal: typeof SearchDialogModal }

export { SearchDialogModal, type SearchDialogModalType }
